import React, { useCallback } from 'react';
import { usePrivateAction } from 'hooks/usePrivateAction';
import { noop } from 'utils/noop';
import { AnyAuthenticatedUserAction } from 'types';

export type PrivateActionClickHandlerParams = {
    authOnly?: boolean;
    onClick: (e: React.MouseEvent) => void;
    preventDefault?: boolean;
    stopPropagation?: boolean;
    isLoading?: boolean;
    userAction: AnyAuthenticatedUserAction;
    analyticsCallback?: () => void;
};

export const usePrivateActionClickHandler = ({
    authOnly = false,
    onClick,
    preventDefault = true,
    stopPropagation = true,
    analyticsCallback = noop,
    isLoading = false,
    userAction,
}: PrivateActionClickHandlerParams): ((e: React.MouseEvent) => void) => {
    const doPrivateAction = usePrivateAction();

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            preventDefault && e.preventDefault?.();
            stopPropagation && e.stopPropagation?.();

            if (isLoading) {
                return;
            }

            if (authOnly) {
                doPrivateAction({
                    handleAuthenticated: () => onClick(e),
                    actionDescription: userAction,
                });
            } else {
                onClick(e);
            }

            analyticsCallback();
        },
        [
            preventDefault,
            stopPropagation,
            authOnly,
            isLoading,
            analyticsCallback,
            doPrivateAction,
            onClick,
            userAction,
        ],
    );

    return handleClick;
};
